import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Card, Collapse, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { selectMultiStationData, selectObservationTypes, selectData } from '@awp/stores/dataVisualizationSlice';
import GraphLegend from '@awp/components/Graphs/MultiStation/Legend';
import Graph from '@awp/components/Graphs/Common/Graph';
import { selectAllWeatherStations } from '@stores/weatherStationSlice';
import { useMultiStationContext } from '@awp/components/Graphs/MultiStation/MultiStationContext';
import { IconTableColumns } from '@awp/components/awpIcons';
import Color from 'color';
import { DATASET_TYPES, FROST_PROBE_CONFIG, DATASET_CONFIG, WEATHERNET_PAVEMENT } from '@awp/constants';
import { isWxnetDataset, isSelectedWxnetDataset } from '@awp/helpers/utilsReact';
import { useWeatherForecastContext } from './WeatherForecastContext';
import { selectAllTypes } from '@stores/sensorModelSlice';

/**
 * React component for rendering a graph container with optional legend and toggles.
 * This component integrates various configurations and displays them based on user interaction and data availability.
 *
 * @param {Object} props - The props object containing configuration for the graph.
 * @param {Object} props.dataGraph - Object containing specific settings and data references for the graph.
 * @returns {JSX.Element} The JSX element representing the graph container, possibly including a legend.
 */
const ForecastContainer = (props) => {
  // Context and selector hooks to fetch state from Redux and context.
  const { isSingleStation, singleStationId, singleHourlyEnd, singleDisplayRange } = props;
  const { weatherStations, weatherStationId, heatMapObservation, forecastWeatherStationId } = isSingleStation
    ? { weatherStations: null, weatherStationId: null, heatMapObservation: null, forecastWeatherStationId: null }
    : useMultiStationContext();
  const { hourlyEnd } = isSingleStation ? { hourlyEnd: null } : useWeatherForecastContext();
  const allWeatherStations = useSelector(selectAllWeatherStations);
  const allObservationTypes = useSelector(selectObservationTypes);
  const data = useSelector(selectMultiStationData);
  const sensorTypes = useSelector(selectAllTypes);

  // Local state for handling UI toggles and selections
  const [selectedForecastOptions, setSelectedForecastOptions] = useState([]);
  const showLegend = false;
  const [showLegendLocal, setShowLegendLocal] = useState(showLegend);
  // Calculate dynamic widths for responsive layout based on legend visibility
  const graphWidth = showLegendLocal ? 9 : 12;
  const GenerateDataGraph = (id, displayOrder, weatherStationInfo) => {
    const dg = {
      DataGraphId: -999,
      DisplayOrder: 1,
      Name: null,
      Description: null,
      DataGraphViewId: null,
      DataGraphPresetId: -999,
      Concurrency: 1,
      PersistentGraphFilters: [],
    };
    dg.DataGraphId = id;
    dg.DataGraphPresetId = id;
    dg.DisplayOrder = displayOrder;

    let observationTypeName = 'sfc_temp';
    const pgf = GeneratePersistentGraphFilter(id, observationTypeName, weatherStationInfo);
    pgf.ColorCodeOverride = WEATHERNET_PAVEMENT.TEMP_MEASURED_HSL;
    dg.PersistentGraphFilters.push(pgf);
    observationTypeName = 'surfaceTemp';
    const pgf2 = GeneratePersistentGraphFilter(id, observationTypeName, weatherStationInfo);
    dg.PersistentGraphFilters.push(pgf2);

    observationTypeName = 'sfc_stat_derived_state';
    const pgf3 = GeneratePersistentGraphFilter(id, observationTypeName, weatherStationInfo);
    dg.PersistentGraphFilters.push(pgf3);
    observationTypeName = 'surfaceCondition';
    const pgf4 = GeneratePersistentGraphFilter(id, observationTypeName, weatherStationInfo);
    pgf4.ColorCodeOverride = WEATHERNET_PAVEMENT.STATUS_FORECASTED_HSL;
    dg.PersistentGraphFilters.push(pgf4);
    observationTypeName = 'sfc_condn_derived_state';
    const pgf5 = GeneratePersistentGraphFilter(id, observationTypeName, weatherStationInfo);
    dg.PersistentGraphFilters.push(pgf5);
    return dg;
  };

  const GeneratePersistentGraphFilter = (id, observationTypeName, weatherStationInfo) => {
    const pgf = {
      GraphFilterId: -999,
      DisplayOrder: null,
      DisplayName: null,
      PlacementName: DATASET_CONFIG.WxNetPavement,
      ColorCodeOverride: null,
      DataGraphId: -999,
      ObservationTypeId: null,
      WeatherStationId: null,
      Concurrency: 1,
      weatherStationInfo: null,
    };
    pgf.GraphFilterId = id;
    pgf.DataGraphId = id;
    pgf.WeatherStationId = weatherStationInfo?.Id;
    pgf.weatherStationInfo = weatherStationInfo;
    if (allObservationTypes) {
      const ot = allObservationTypes.find((o) => o.ObservationTypeName === observationTypeName);
      pgf.ObservationTypeId = ot?.ObservationTypeId;
    }
    return pgf;
  };
  const forecastWeatherStation = isSingleStation
    ? allWeatherStations.find((wx) => wx.Id === singleStationId)
    : allWeatherStations.find((wx) => wx.Id === forecastWeatherStationId);
  const forecastWeatherStationInfo = forecastWeatherStation
    ? {
        DropdownText: forecastWeatherStation.DropdownText,
        Id: forecastWeatherStation.Id,
        Name: forecastWeatherStation.Name,
        Number: forecastWeatherStation.Number,
      }
    : null;

  const dataGraph = GenerateDataGraph(-999, 1, forecastWeatherStationInfo);

  const BuildSelectedOptions = (_dataGraph) => {
    const __dataGraph = _dataGraph;
    const graphFilters = __dataGraph?.PersistentGraphFilters;
    // /**
    //  * Constructs the datasets for the graph by filtering and mapping the necessary data
    //  * points from the available data based on the active graph filters.
    //  */
    const results = graphFilters
      .map((ds) => {
        const dataDTO = data.DataVisualizationDTOs.find((d) => d.WeatherStationId === ds.WeatherStationId);
        const obs = dataDTO?.Datasets.filter((s) => {
          // Check if the PlacementName matches and has the same observationTypeId
          //If it is weatherNet pavement, check if the dataset belongs to the selected station in forecast panel
          //below scenarios:
          //regular dataset(s) || wxnet pavement dataset(s) (non-deferred)
          if (
            //s.PlacementName === ds.PlacementName &&
            isWxnetDataset(s) //||
            //isSelectedWxnetDataset(s, ds, forecastWeatherStationId)
          ) {
            return s.observationTypeId === ds.ObservationTypeId;
          }
          // Default case to handle unexpected scenarios (this can be omitted if unnecessary)
          return false;
        });
        return (
          obs?.map((o) => ({
            ...o,
            graphConfig: {
              ...o.graphConfig,
              graphColor: ds.ColorCodeOverride || o.graphConfig.graphColor,
              colorOverridden: !!ds.ColorCodeOverride,
            },
            weatherStationInfo: ds.weatherStationInfo,
          })) || []
        );
      })
      .flat()
      .filter((e) => e !== undefined);
    const options = results.map((ds) => ({
      group: ds.group,
      typeId: ds.observationTypeId,
      value: ds.DatasetId,
      label: ds.label,
      color: ds.graphConfig.graphColor || '#000',
      colorOverridden: ds.graphConfig.colorOverridden,
      weatherStationInfo: ds.weatherStationInfo,
      weatherStationId: ds.weatherStationInfo?.Id,
      weatherStationText: ds.weatherStationInfo?.DropdownText,
      sensorType: sensorTypes?.find((sensor) => sensor.Id === ds.SensorTypeId),
    }));
    //sort by labels for color adjustment
    //options.sort((a, b) => a.label.localeCompare(b.label));
    options.sort((a, b) => {
      // First compare the labels
      const labelComparison = a.label.localeCompare(b.label);
      if (labelComparison !== 0) {
        return labelComparison;
      }
      // If labels are the same, compare the groups
      return a.group.localeCompare(b.group);
    });
    // Adjust colors for readability and visual distinction.
    const groupedObjects = options.reduce((acc, obj) => {
      const key = obj.typeId + obj.color;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
    let adjustedColors = Object.values(groupedObjects).flatMap((objs) => {
      let { h, s, l } = Color(objs[0].color).hsl().object();
      const origL = l;
      let tintAdj = Math.round((85 - l) / objs.length);
      return objs.map((obj) => {
        if (Math.min(l, 90) > 90) {
          l = origL;
        }
        obj.color = Color({ h, s, l }).hex().toLowerCase();
        l += tintAdj;
        return obj;
      });
    });
    //check to remove any duplicated observations
    // caused by deferred setting same observations that may already be preset (WEAV-14091)
    adjustedColors = adjustedColors.filter(
      (
        (seen) => (item) =>
          !seen.has(item.value + item.weatherStationId) && seen.add(item.value + item.weatherStationId)
      )(new Set()),
    );
    //re-apply the sort after color adjustments
    adjustedColors.sort((a, b) => {
      // First compare the labels
      const labelComparison = a.label.localeCompare(b.label);
      if (labelComparison !== 0) {
        return labelComparison;
      }
      // If labels are the same, compare the groups
      return a.group.localeCompare(b.group);
    });
    return adjustedColors;
  };

  useEffect(() => {
    // Early exit if data is not yet available to prevent errors.
    if (!data) return;
    const _forecastOptions = BuildSelectedOptions(dataGraph);
    setSelectedForecastOptions(_forecastOptions);
  }, [data]);
  return (
    <>
      {selectedForecastOptions.length > 0 && data?.DataVisualizationDTOs ? (
        <Card border="light" className="page-card">
          <Card.Body>
            <div className={`graph__container ${showLegendLocal ? 'show-legend' : ''}`}>
              <Row>
                <Col lg={graphWidth} md={graphWidth} className="container-transition graph-transition">
                  {((isSingleStation && singleHourlyEnd > 0) || (!isSingleStation && hourlyEnd > 0)) && (
                    <Graph
                      dataGraph={dataGraph}
                      selectedOptions={selectedForecastOptions}
                      isForecastGraph={true}
                      hourlyEnd={isSingleStation ? singleHourlyEnd : hourlyEnd}
                      isSingleStation={isSingleStation}
                      singleStationId={singleStationId}
                      singleDataRange={{ endDate: singleHourlyEnd?.value, displayRange: singleDisplayRange }}
                    />
                  )}
                </Col>
              </Row>
            </div>
          </Card.Body>
        </Card>
      ) : null}
    </>
  );
};

export default ForecastContainer;
